import * as React from 'react';
import { Card, Loading, Table, Input, Page, notify } from 'sha-el-design';
import { nestedAccess } from 'sha-el-design/lib/helpers';
import * as moment from 'moment';
import { GraphqlQuery } from 'requestapijs';
import { Colums } from 'sha-el-design/lib/components/Table/Table';
import { Link } from 'react-router-dom';
import {
  StateNodeEdge,
  TownNodeEdge,
  AreaNodeEdge,
  CallHistoryNodeConnection,
  CallHistoryNodeEdge,
  RetailerNodeEdge,
} from '../../../../schema';
import { TownDropdown } from '../../Common/TownDropdown';
import { AreaDropdownSelect } from '../../Common/AreaDropdownSelect';
import { StateDropdown } from '../../Common/StateDropdown';
import { Pagination } from '../../Common/Pagination';
import { RetailerDropdown } from '../../Common/RetailerDropdown';
import { RetailerReportQuery } from './constants';
import { statusPill } from '../statusPill';
import { deep } from '../deep';
import { IST_DAY_START, IST_DAY_END } from '../constants';
import {
  FMT, PRESETS, RangePresets, activePresetKey,
  statTile, connectRate, groupNum,
  durationSeconds, shortDuration, decorateRows, clickedRowIndex,
} from '../reportBits';
import { MdArrowBack, MdHome, MdChevronRight } from 'react-icons/md';
import '../../Common/listPageStyles';

export class RetailerReport extends React.Component<Props, State> {

  constructor(props: Props) {
    super(props);
    const from = moment().subtract(29, 'days').format(FMT);
    const to = moment().format(FMT);
    const blank = { state: null, town: null, area: null, retailer: null };
    this.state = {
      // Draft (what the controls show) vs applied (what the query runs).
      ...blank,
      fromDate: from,
      toDate: to,
      applied: { ...blank, fromDate: from, toDate: to },
      after: '',
    };
  }

  rangeIsValid = () => {
    const { fromDate, toDate } = this.state;
    return !!fromDate && !!toDate && !moment(fromDate).isAfter(moment(toDate));
  }

  isDirty = () => {
    const { state, town, area, retailer, fromDate, toDate, applied } = this.state;
    const id = (v: any) => nestedAccess(v, 'node', 'id') || '';
    return fromDate !== applied.fromDate
      || toDate !== applied.toDate
      || id(state) !== id(applied.state)
      || id(town) !== id(applied.town)
      || id(area) !== id(applied.area)
      || id(retailer) !== id(applied.retailer);
  }

  applied = () => {
    const { state, town, area, retailer, fromDate, toDate } = this.state;
    return { state, town, area, retailer, fromDate, toDate };
  }

  applyPreset = (preset: typeof PRESETS[0]) => {
    const [a, b] = preset.range();
    const fromDate = a.format(FMT);
    const toDate = b.format(FMT);
    this.setState({
      fromDate, toDate,
      applied: { ...this.applied(), fromDate, toDate },
      after: '',
    });
  }

  search = () => {
    if (!this.rangeIsValid()) {
      return notify({
        type: 'error',
        title: 'Check the dates',
        message: 'From Date must be on or before To Date.',
        duration: 3000,
      });
    }
    this.setState({ applied: this.applied(), after: '' });
  }

  onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') { this.search(); }
  }

  clearFilters = () => {
    const blank = { state: null, town: null, area: null, retailer: null };
    const { fromDate, toDate } = this.state;
    this.setState({
      ...blank,
      applied: { ...blank, fromDate, toDate },
      after: '',
    } as any);
  }

  private tableRef: HTMLDivElement = null;
  private rows: any[] = [];

  goToQnA = (node: any) => {
    if (!node || node.callStatus !== 'ANSWERED') { return; }
    const to = `/dashboard/retailer_report/${node.id}`;
    (this.props as any).history
      ? (this.props as any).history.push(to)
      : window.location.assign(to);
  }

  columns = (): Array<Colums<CallHistoryNodeEdge>> => [{
    key: 'call_date',
    header: 'Called',
    render: (text, obj) => (
      <div>
        <span>{obj.node.callDate ? moment(obj.node.callDate).format('D MMM YYYY') : '—'}</span>
        {obj.node.callStart &&
          <p className='cs-q__desc'>{moment(obj.node.callStart).format('h:mm a')}</p>}
      </div>
    ),
  }, {
    key: 'caller_name',
    header: 'Caller',
    render: (text, obj) => {
      const callerId = deep(obj.node, 'workHistory', 'work', 'caller', 'id');
      const callerName = deep(obj.node, 'workHistory', 'work', 'caller', 'employee', 'name');
      // Was an unguarded obj.node.workHistory.work.caller.id -- a call whose
      // allocation has since been deleted took the whole table down with it.
      if (!callerId) {
        return <span className='cs-rep__muted'>{callerName || 'Unknown'}</span>;
      }
      return (
        <Link className='cs-q__name' to={`/dashboard/call_history_report/${callerId}`}>
          {callerName || 'Unnamed caller'}
        </Link>
      );
    },
  }, {
    key: 'mobile_number',
    header: 'Mobile Number',
    render: (text, obj) => obj.node.mobileNumber || '—',
  }, {
    key: 'status',
    header: 'Status',
    render: (text, obj) => statusPill(obj.node.callStatus),
  }, {
    key: 'duration',
    header: 'Duration',
    render: (text, obj) => (
      obj.node.callStatus !== 'ANSWERED'
        ? <span className='cs-rep__num cs-rep__num--zero'>—</span>
        : <span className='cs-rep__num'>{shortDuration(durationSeconds(obj.node.duration))}</span>
    ),
  }, {
    key: 'qna',
    header: '',
    render: (text, obj) => (
      // Only answered calls open the questionnaire, so only they can have a
      // response to view.
      obj.node.callStatus === 'ANSWERED'
        ? <span className='cs-rep__go'><MdChevronRight /></span>
        : null
    ),
  }]

  columnsWithRetailer = (): Array<Colums<CallHistoryNodeEdge>> => [{
    key: 'retailer',
    header: 'Retailer',
    render: (text, obj) => (
      <span className='cs-q__name'>{nestedAccess(obj.node, 'retailer', 'firm') || 'Unknown'}</span>
    ),
  }, ...this.columns()]

  // Unlike the caller screens there is no server-side aggregate for an
  // arbitrary state/town/area/retailer combination, so these are computed from
  // the rows actually loaded -- and say so when more pages exist rather than
  // presenting a page total as if it were the whole answer.
  tiles = (edges: any[], hasNextPage: boolean) => {
    let answered = 0; let noReply = 0; let wrong = 0; let talk = 0;
    const retailers: { [k: string]: boolean } = {};
    edges.forEach((e: any) => {
      const n = e.node;
      if (n.callStatus === 'ANSWERED') { answered += 1; talk += durationSeconds(n.duration); }
      if (n.callStatus === 'NO_REPLY') { noReply += 1; }
      if (n.callStatus === 'WRONG_NUMBER') { wrong += 1; }
      const firm = nestedAccess(n, 'retailer', 'firm');
      if (firm) { retailers[firm] = true; }
    });
    const pct = connectRate(answered, noReply, wrong);
    const scope = hasNextPage ? 'on this page only' : 'across all matching calls';
    return (
      <div className='cs-stats'>
        {statTile('calls', 'Calls', groupNum(edges.length), scope)}
        {statTile('answered', 'Answered', groupNum(answered),
          `${groupNum(noReply)} no reply · ${groupNum(wrong)} wrong number`)}
        {statTile('rate', 'Connect rate', pct === null ? '—' : `${pct}%`,
          'of dialled numbers answered')}
        {statTile('alert', 'Talk time', shortDuration(talk),
          `${groupNum(Object.keys(retailers).length)} retailer${Object.keys(retailers).length === 1 ? '' : 's'}`)}
      </div>
    );
  }

  breadcrumbs = () => ({
    seperator: '/',
    paths: [
      { text: <MdHome />, link: '/dashboard' },
      { text: 'Retailer Report', link: '/dashboard/retailer_report' },
    ].map((v) => ({
      render: () => (
        <Link key={v.link} to={v.link}>
          {v.text}
        </Link>
      ),
    })),
  })

  render() {
    const { state, town, area, fromDate, toDate, retailer, applied, after } = this.state;
    const validRange = this.rangeIsValid();
    const appliedRetailerId = nestedAccess(applied.retailer, 'node', 'id');
    const hasFilters = !!(applied.state || applied.town || applied.area || applied.retailer);
    return (
      <div className='cs-list'>
        <Page
          title={'Retailer Call History'}
          breadcrumbs={this.breadcrumbs()}
          backIcon={
            <Link to={'/dashboard'}>
              <MdArrowBack />
            </Link>
          }
        >
          <Card>
            <RangePresets
              activeKey={activePresetKey(applied.fromDate, applied.toDate)}
              onPick={this.applyPreset}
            />

            {/* .cs-rep__field wrappers: sha-el's Input emits a container AND a
                help div as siblings, which would otherwise take two grid
                columns each and knock the row out of alignment. */}
            <div className='cs-rep__filters cs-rep__filters--wide' onKeyDown={this.onKeyDown}>
              <div className='cs-rep__field'>
                <StateDropdown
                  mode='single'
                  value={state}
                  onChange={(v) => this.setState({ state: v, town: null, area: null })}
                  uniqueIdentifier={(v) => v && v.node.id}
                  displayProp={(v) => (v && v.node.name) || ''}
                  label='State'
                />
              </div>
              <div className='cs-rep__field'>
                {state ? (
                  <TownDropdown
                    mode='single'
                    selectedState={nestedAccess(state, 'node', 'id')}
                    value={town}
                    onChange={(v) => this.setState({ town: v, area: null })}
                    uniqueIdentifier={(v) => v && v.node.id}
                    displayProp={(v) => (v && v.node.name) || ''}
                    label='Town'
                  />
                ) : (
                  // Kept in the layout rather than appearing from nowhere, so
                  // the row does not reflow as you fill it in.
                  <Input label='Town' value='' disabled placeholder='Pick a state first' />
                )}
              </div>
              <div className='cs-rep__field'>
                {town ? (
                  <AreaDropdownSelect
                    selectedTown={nestedAccess(town, 'node', 'id')}
                    value={area}
                    mode='single'
                    onChange={(v) => this.setState({ area: v })}
                    uniqueIdentifier={(v) => v && v.node.id}
                    displayProp={(v) => (v && v.node.name) || ''}
                    label='Area'
                  />
                ) : (
                  <Input label='Area' value='' disabled placeholder='Pick a town first' />
                )}
              </div>
              <div className='cs-rep__field'>
                <RetailerDropdown
                  mode='single'
                  value={retailer}
                  onChange={(v) => this.setState({ retailer: v })}
                  uniqueIdentifier={(v) => v && v.node.id}
                  displayProp={(v) => (v && v.node.firm) || ''}
                  label='Retailer'
                />
              </div>
              <div className='cs-rep__field'>
                <Input
                  type='date'
                  label='From Date'
                  value={fromDate}
                  onChange={(e) => this.setState({ fromDate: e.target.value })}
                />
              </div>
              <div className='cs-rep__field'>
                <Input
                  type='date'
                  label='To Date'
                  value={toDate}
                  onChange={(e) => this.setState({ toDate: e.target.value })}
                />
              </div>
              <button
                type='button'
                className='cs-rep__search'
                onClick={this.search}
                disabled={!validRange}
              >
                Search
              </button>
            </div>

            <p className='cs-rep__scope'>
              <span>
                Calls placed between <b>{moment(applied.fromDate).format('D MMM YYYY')}</b> and{' '}
                <b>{moment(applied.toDate).format('D MMM YYYY')}</b>
                {appliedRetailerId
                  ? <> to <b>{nestedAccess(applied.retailer, 'node', 'firm')}</b></> : ''}
                {nestedAccess(applied.area, 'node', 'name')
                  ? <> in <b>{nestedAccess(applied.area, 'node', 'name')}</b></>
                  : nestedAccess(applied.town, 'node', 'name')
                    ? <> in <b>{nestedAccess(applied.town, 'node', 'name')}</b></>
                    : nestedAccess(applied.state, 'node', 'name')
                      ? <> in <b>{nestedAccess(applied.state, 'node', 'name')}</b></> : ''}.
                {' '}Answered calls open their questionnaire answers.
              </span>
              {!validRange && <span className='cs-rep__warn'>From Date is after To Date</span>}
              {validRange && this.isDirty() &&
                <span className='cs-rep__dirty'>Press Search to apply</span>}
              {hasFilters && (
                <button type='button' className='cs-chip' onClick={this.clearFilters}>
                  Clear filters
                </button>
              )}
            </p>

            <GraphqlQuery
              key='query'
              queryString={RetailerReportQuery}
              variables={{
                state: nestedAccess(applied.state, 'node', 'id'),
                town: nestedAccess(applied.town, 'node', 'id'),
                area: nestedAccess(applied.area, 'node', 'id'),
                retailerId: appliedRetailerId,
                fromDate: `${applied.fromDate}${IST_DAY_START}`,
                toDate: `${applied.toDate}${IST_DAY_END}`,
                after,
              }}
              render={(response: { allCallHistorys: CallHistoryNodeConnection }, error, loading) => {
                const edges = nestedAccess(response, 'allCallHistorys', 'edges');
                if (error || (!loading && !edges)) {
                  return (
                    <div className='cs-list__empty'>
                      <h4>Could not load these calls</h4>
                      <p>Check the filters and try again.</p>
                    </div>
                  );
                }
                const hasNext = !!nestedAccess(response, 'allCallHistorys', 'pageInfo', 'hasNextPage');
                return (
                  <Loading
                    isLoading={loading}
                    render={() => {
                      this.rows = edges;
                      return (
                        <>
                          {edges.length ? (
                            <>
                              {this.tiles(edges, hasNext)}
                              <div
                                className='cs-list--stack'
                                ref={(el) => {
                                  this.tableRef = el;
                                  if (el) { decorateRows(el, { clickable: true }); }
                                }}
                                onClick={(e) => {
                                  const i = clickedRowIndex(this.tableRef, e.target as HTMLElement);
                                  if (i >= 0 && this.rows[i]) { this.goToQnA(this.rows[i].node); }
                                }}
                              >
                                <Table
                                  data={edges}
                                  columns={appliedRetailerId ? this.columns() : this.columnsWithRetailer()}
                                />
                              </div>
                            </>
                          ) : (
                            <div className='cs-list__empty'>
                              <h4>No calls match these filters</h4>
                              <p>
                                Try a wider range — <b>All time</b> shows everything on record —
                                or clear the state and retailer filters.
                              </p>
                            </div>
                          )}
                          <Pagination
                            key={[applied.fromDate, applied.toDate, appliedRetailerId,
                              nestedAccess(applied.state, 'node', 'id'),
                              nestedAccess(applied.town, 'node', 'id'),
                              nestedAccess(applied.area, 'node', 'id')].join('|')}
                            endCursor={nestedAccess(response, 'allCallHistorys', 'pageInfo', 'endCursor')}
                            hasNextPage={hasNext}
                            after={(afterValue) => this.setState({ after: afterValue })}
                          />
                        </>
                      );
                    }}
                  />
                );
              }}
            />
          </Card>
        </Page>
      </div>
    );
  }
}

interface AppliedFilters {
  state: StateNodeEdge;
  town: TownNodeEdge;
  area: AreaNodeEdge;
  retailer: RetailerNodeEdge;
  fromDate: string;
  toDate: string;
}

interface State {
  state: StateNodeEdge;
  town: TownNodeEdge;
  area: AreaNodeEdge;
  fromDate: string;
  toDate: string;
  retailer: RetailerNodeEdge;
  applied: AppliedFilters;
  after: string;
}

interface Props {
}
